import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { getProductImages } from "../Helpers/ProductImages"
import { IPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { PublicMenuImagePreviewPupop } from "../Pupop/PublicMenuImagePreviewPupop"

interface ProductImageCarouselProps {
  product: IProductRequest
  compact?: boolean
  theme?: IPublicMenuTheme | null
}

const ProductImageCarouselContent: React.FC<ProductImageCarouselProps> = ({ product, compact = false, theme }) => {
  const { t } = useTranslation()

  const [activeIndex, setActiveIndex] = useState(0)

  const [failedImages, setFailedImages] = useState<string[]>([])

  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(null)

  const images = useMemo(() => {
    return getProductImages(product).filter((image) => !failedImages.includes(image))
  }, [failedImages, product])

  useEffect(() => {
    if (images.length <= 1) return

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length)
    }, 3000)

    return () => window.clearInterval(interval)
  }, [images.length])

  if (images.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50 text-gray-300">
        <span className={compact ? "text-3xl opacity-50" : "text-5xl opacity-20"}>🍽️</span>

        {!compact && (
          <span className="py-5 text-[16px] font-black uppercase tracking-widest opacity-100">
            {t("publicMenu:menu_card.no_image")}
          </span>
        )}
      </div>
    )
  }

  const displayedIndex = Math.min(activeIndex, images.length - 1)

  return (
    <>
      <div className="relative h-full w-full overflow-hidden bg-white">
        <div
          className="flex h-full transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${displayedIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <button
              key={image}
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setExpandedImageIndex(index)
              }}
              aria-label={t("publicMenu:add_meal_gallery.image_alt", { name: product.name, index: index + 1 })}
              className="h-full w-full shrink-0 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
              data-interaction-description-key="interactionDescriptions:publicMenu.components.productImageCarousel.button.imageAlt"
            >
              <img
                src={image.toClientRequest()}
                alt={t("genericFoodEntity:label_image_position", { index: index + 1 })}
                loading="lazy"
                decoding="async"
                onError={() => setFailedImages((current) => (current.includes(image) ? current : [...current, image]))}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>

        {images.length > 1 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
            {images.map((image, index) => (
              <span
                key={image}
                className={`h-1.5 rounded-full shadow transition-all duration-300 ${
                  displayedIndex === index ? "w-5 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {expandedImageIndex !== null && (
        <PublicMenuImagePreviewPupop
          images={images}
          initialIndex={expandedImageIndex}
          title={product.name}
          theme={theme}
          onClose={() => setExpandedImageIndex(null)}
        />
      )}
    </>
  )
}

const ProductImageCarousel: React.FC<ProductImageCarouselProps> = (props) => {
  const imageKey = [props.product.id, props.product.image, ...(props.product.images ?? [])].join("|")

  return <ProductImageCarouselContent key={imageKey} {...props} />
}

export default ProductImageCarousel
